import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "lib/redux/store";

// Import template list to access template configurations
const getTemplateList = () => {
  // This is a simplified version - we'll need to access the actual templateList
  const templateConfigs: {[key: number]: {fontFamily: string, fontSize: string, themeColor: string}} = {
    1: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    2: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    3: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    4: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    5: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    6: { fontFamily: "Roboto", fontSize: "11", themeColor: "#000000" },
    7: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    8: { fontFamily: "Roboto", fontSize: "11", themeColor: "#6565e7" },
    9: { fontFamily: "PlayfairDisplay", fontSize: "8", themeColor: "#ef4444" },
    11: { fontFamily: "Lora", fontSize: "10", themeColor: "#ef4444" },
    12: { fontFamily: "PlayfairDisplay", fontSize: "11", themeColor: "#0d2867" },
    13: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#15803d" },
    14: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#15803d" },
    15: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#0ea5e9" },
    16: { fontFamily: "PlayfairDisplay", fontSize: "10", themeColor: "#15803d" },
    17: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#0ea5e9" },
    18: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#0ea5e9" },
    19: { fontFamily: "Montserrat", fontSize: "10", themeColor: "#000000" },
    20: { fontFamily: "Roboto", fontSize: "11", themeColor: "#38bdf8" },
    21: { fontFamily: "Montserrat", fontSize: "9", themeColor: "#f97316" },
  };
  return templateConfigs;
};

export interface Settings {
  themeColor: string;
  fontFamily: string;
  fontSize: string;
  documentSize: string;
  formToShow: {
    workExperiences: boolean;
    educations: boolean;
    projects: boolean;
    skills: boolean;
    certifications: boolean;
    languages: boolean;
    awards: boolean;
    custom: boolean;
  };
  formToHeading: {
    workExperiences: string;
    educations: string;
    projects: string;
    skills: string;
    certifications: string;
    languages: string;
    awards: string;
    custom: string;
  };
  formsOrder: ShowForm[];
  showBulletPoints: {
    educations: boolean;
    projects: boolean;
    skills: boolean;
    certifications: boolean;
    languages: boolean;
    awards: boolean;
    custom: boolean;
  };
  isImportComponentOpen: boolean; // Add new state property
  currentSection: string; // Add new state property
  previousSection: string;
  progress: number; // Add new state property
  isProjectRecommendationsOpen: boolean; // Add new state property
  template: number;
  isReset: boolean;
  scale: number;
  isSidebarOpen: boolean;
  leftRightFormOrder?: {
    left: ShowForm[];
    right: ShowForm[];
  };
}

export type ShowForm = keyof Settings["formToShow"];
export type FormWithBulletPoints = keyof Settings["showBulletPoints"];
export type GeneralSetting = Exclude<
  keyof Settings,
  "formToShow" | "formToHeading" | "formsOrder" | "showBulletPoints"
>;

export const DEFAULT_THEME_COLOR = "#38bdf8"; // sky-400
export const DEFAULT_FONT_FAMILY = "Roboto";
export const DEFAULT_FONT_SIZE = "11"; // text-base https://tailwindcss.com/docs/font-size
export const DEFAULT_FONT_COLOR = "#171717"; // text-neutral-800

export const initialSettings: Settings = {
  themeColor: DEFAULT_THEME_COLOR,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: DEFAULT_FONT_SIZE,
  documentSize: "A4",
  formToShow: {
    workExperiences: true,
    educations: true,
    projects: true,
    skills: true,
    certifications: false,
    languages: false,
    awards: false,
    custom: false,
  },
  formToHeading: {
    workExperiences: "WORK EXPERIENCE",
    educations: "EDUCATION",
    projects: "PROJECTS",
    skills: "SKILLS",
    certifications: "CERTIFICATE",
    languages: "LANGUAGE",
    awards: "AWARD",
    custom: "CUSTOM SECTION",
  },
  formsOrder: [
    "workExperiences",
    "educations",
    "projects",
    "skills",
    "certifications",
    "languages",
    "awards",
    "custom",
  ],
  showBulletPoints: {
    educations: true,
    projects: true,
    skills: true,
    certifications: true,
    languages: true,
    awards: true,
    custom: true,
  },
  isImportComponentOpen: false, // Initial value for new state property
  currentSection: "templates", // Default section
  previousSection: "templates",
  progress: 0, // Initial value for new state property
  isProjectRecommendationsOpen: false, // Initial value for new state property
  template: 1,
  isReset: false,
  scale: 0.8,
  isSidebarOpen: true,
  leftRightFormOrder: {
    left: [],
    right: [],
  },
};

export const settingsSlice = createSlice({
  name: "settings",
  initialState: initialSettings,
  reducers: {
    changeSettings: (
      draft,
      action: PayloadAction<{ field: GeneralSetting; value: string }>
    ) => {
      const { field, value } = action.payload;
      (draft[field] as string) = value;
    },
    changeShowForm: (
      draft,
      action: PayloadAction<{ field: ShowForm; value: boolean }>
    ) => {
      const { field, value } = action.payload;
      draft.formToShow[field] = value;
    },
    changeFormHeading: (
      draft,
      action: PayloadAction<{ field: ShowForm; value: string }>
    ) => {
      const { field, value } = action.payload;
      draft.formToHeading[field] = value;
    },
    changeFormOrder: (
      draft,
      action: PayloadAction<{ form: ShowForm; type: "up" | "down" }>
    ) => {
      const { form, type } = action.payload;
      const lastIdx = draft.formsOrder.length - 1;
      const pos = draft.formsOrder.indexOf(form);
      const newPos = type === "up" ? pos - 1 : pos + 1;
      const swapFormOrder = (idx1: number, idx2: number) => {
        const temp = draft.formsOrder[idx1];
        draft.formsOrder[idx1] = draft.formsOrder[idx2];
        draft.formsOrder[idx2] = temp;
      };
      if (newPos >= 0 && newPos <= lastIdx) {
        swapFormOrder(pos, newPos);
      }
    },
    changeLeftRightFormOrder: (
      draft,
      action: PayloadAction<{
        form: ShowForm;
        column: "left" | "right";
        direction: "up" | "down";
      }>
    ) => {
      const { form, column, direction } = action.payload;
      // swap form order either in left or right column based on column received in payload, basically manipulate leftRightFormOrder
      if (draft && draft.leftRightFormOrder) {
        // Check if draft and leftRightFormOrder are defined
        const targetColumn =
          column === "left"
            ? draft.leftRightFormOrder.left
            : draft.leftRightFormOrder.right;
        const pos = targetColumn.indexOf(form);
        const lastIdx = targetColumn.length - 1;
        const newPos = direction === "up" ? pos - 1 : pos + 1;

        const swapFormOrder = (idx1: number, idx2: number) => {
          const temp = targetColumn[idx1];
          targetColumn[idx1] = targetColumn[idx2];
          targetColumn[idx2] = temp;
        };

        // Check if newPos is valid and within bounds
        if (newPos >= 0 && newPos <= lastIdx && pos !== -1) {
          swapFormOrder(pos, newPos);
        }
      }
    },
    setLeftRightFormOrder: (
      draft,
      action: PayloadAction<{ left: ShowForm[]; right: ShowForm[] }>
    ) => {
      draft.leftRightFormOrder = action.payload;
    },
    changeShowBulletPoints: (
      draft,
      action: PayloadAction<{
        field: FormWithBulletPoints;
        value: boolean;
      }>
    ) => {
      const { field, value } = action.payload;
      draft["showBulletPoints"][field] = value;
    },
    setSettings: (draft, action: PayloadAction<Settings>) => {
      return action.payload;
    },
    toggleImportComponent: (draft) => {
      draft.isImportComponentOpen = !draft.isImportComponentOpen;
    },
    setImportComponentOpen: (draft, action: PayloadAction<boolean>) => {
      draft.isImportComponentOpen = action.payload;
    },
    setCurrentSection: (state, action: PayloadAction<string>) => {
      state.previousSection = state.currentSection; // Update previousSection
      state.currentSection = action.payload; // Update currentSection
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    toggleProjectRecommendations: (draft) => {
      draft.isProjectRecommendationsOpen = !draft.isProjectRecommendationsOpen;
    },
    setTemplate: (state, action: PayloadAction<number>) => {
      state.template = action.payload;
    },
    setTemplateWithDefaults: (state, action: PayloadAction<number>) => {
      const templateId = action.payload;
      state.template = templateId;
      
      // Apply template defaults (fontFamily, fontSize, themeColor)
      const templateConfigs = getTemplateList();
      const templateConfig = templateConfigs[templateId];
      
      if (templateConfig) {
        state.fontFamily = templateConfig.fontFamily;
        state.fontSize = templateConfig.fontSize;
        state.themeColor = templateConfig.themeColor;
      }
    },
    toggleReset: (draft) => {
      draft.isReset = !draft.isReset;
    },
    setFormHeadingToDefault: (
      draft,
      action: PayloadAction<{ field: ShowForm[] }>
    ) => {
      const { field } = action.payload;
      // draft.formToHeading[field] = initialSettings.formToHeading[field];
      field.forEach((form) => {
        return (draft.formToHeading[form] =
          initialSettings.formToHeading[form]);
      });
    },
    setScale: (state, action: PayloadAction<number>) => {
      state.scale = action.payload;
    },
    toggleSidebar: (draft) => {
      draft.isSidebarOpen = !draft.isSidebarOpen;
    },
  },
});

export const {
  changeSettings,
  changeShowForm,
  changeFormHeading,
  changeFormOrder,
  changeShowBulletPoints,
  setSettings,
  toggleImportComponent,
  setCurrentSection,
  setProgress,
  toggleProjectRecommendations,
  setTemplate,
  setTemplateWithDefaults,
  toggleReset,
  setFormHeadingToDefault,
  setScale,
  toggleSidebar,
  setImportComponentOpen,
  changeLeftRightFormOrder,
  setLeftRightFormOrder,
} = settingsSlice.actions;

export const selectSettings = (state: RootState) => state.settings;
export const selectThemeColor = (state: RootState) => state.settings.themeColor;

export const selectFormToShow = (state: RootState) => state.settings.formToShow;
export const selectShowByForm = (form: ShowForm) => (state: RootState) =>
  state.settings.formToShow[form];

export const selectFormToHeading = (state: RootState) =>
  state.settings.formToHeading;
export const selectHeadingByForm = (form: ShowForm) => (state: RootState) =>
  state.settings.formToHeading[form];

export const selectFormsOrder = (state: RootState) => state.settings.formsOrder;
export const selectLeftRightFormOrder = (state: RootState) =>
  state.settings.leftRightFormOrder;
export const selectIsFirstForm =
  (form: ShowForm, columns: string) => (state: RootState) => {
    if (columns !== "double") {
      return state.settings.formsOrder[0] === form;
    } else {
      const { left, right } = state.settings.leftRightFormOrder || {
        left: [],
        right: [],
      };
      return left[0] === form || right[0] === form;
    }
  };
export const selectIsLastForm =
  (form: ShowForm, columns: string) => (state: RootState) => {
    if (columns !== "double") {
      return (
        state.settings.formsOrder[state.settings.formsOrder.length - 1] === form
      );
    } else {
      const { left, right } = state.settings.leftRightFormOrder || {
        left: [],
        right: [],
      };
      return left[left.length - 1] === form || right[right.length - 1] === form;
    }
  };
export const selectShowBulletPoints =
  (form: FormWithBulletPoints) => (state: RootState) =>
    state.settings.showBulletPoints[form];

export const selectIsImportComponentOpen = (state: RootState) =>
  state.settings.isImportComponentOpen;

export const selectCurrentSection = (state: RootState) =>
  state.settings.currentSection; // Select currentSection

export const selectPreviousSection = (state: RootState) =>
  state.settings.previousSection;

export const selectProgress = (state: RootState) => state.settings.progress;

export const selectIsProjectRecommendationsOpen = (state: RootState) =>
  state.settings.isProjectRecommendationsOpen;

export const selectTemplate = (state: RootState) => state.settings.template;

export const selectSectionTitles = (state: RootState) => {
  const { formsOrder, formToHeading } = state.settings;

  // Generate section titles based on formsOrder and formToHeading
  const dynamicTitles = formsOrder.map((form) => ({
    id: form,
    title: formToHeading[form],
  }));

  return [
    { id: "personalDetails", title: "PERSONAL DETAILS" },
    ...dynamicTitles,
    { id: "resumeSettings", title: "RESUME SETTINGS" },
  ];
};

export const selectIsReset = (state: RootState) => state.settings.isReset;

export const selectScale = (state: RootState) => state.settings.scale;

export const selectIsSidebarOpen = (state: RootState) =>
  state.settings.isSidebarOpen;

export const selectSectionIndexes = (state: RootState) => {
  // Return Current Section and Previous Section Index
  const sectionTitles = selectSectionTitles(state);
  const currentSection = selectCurrentSection(state);
  const previousSection = selectPreviousSection(state);
  const currentSectionIndex = sectionTitles.findIndex(
    (section) => section.id === currentSection
  );
  const previousSectionIndex = sectionTitles.findIndex(
    (section) => section.id === previousSection
  );
  return { currentSectionIndex, previousSectionIndex };
};

export default settingsSlice.reducer;
